import { Fragment, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useHistory } from 'react-router-dom';
import {
	AUTOCOMPLETE_SEARCH_TYPE,
	BEM_NAME_ELEMENTS,
	DEFAULT_LIST_ITEM,
} from '../_const';
import AsyncAutocomplete from '../components/AsyncAutocomplete';
import {
	searchJobCategoryMappings,
	searchJobCategoryMappingJobTitles,
} from '../_api';
import CircularProgressWithLabel from '../components/CircularProgressWithLabel';
import PageWithBreadcrumbs from '../layouts/PageWithBreadcrumbs';
import JobCategoryMappingDetails from './DetailsView';

/**
 * Epic Job Category Mapping Search page
 * @param {any} props properties from router
 * @returns Job Category Mapping Search page view
 */
const SearchView = props => {
	const history = useHistory();
	const query = new URLSearchParams(props.location.search);
	const id = query.get('id');
	const [sIsResultsExpanded, setIsResultsExpanded] = useState(false);
	const [sDepartmentSearchValue, setDepartmentSearchValue] = useState('');
	const [sJobTitleSearchValue, setJobTitleSearchValue] = useState('');
	const [sIsSearching, setIsSearching] = useState(false);
	const [sSelectionId, setSelectionId] = useState(id ? [id] : null);
	const [sResults, setResults] = useState([]);
	const [duplicateResults, setDuplicateResults] = useState([]);

	/**
	 * Function finds duplicate job categories post search to use later
	 */
	const findDuplicateObjects = results => {
		const seen = new Map();
		const duplicates = new Set();

		results.forEach((item, index) => {
			const key = `${item.department}-${item.jobTitle}`;
			if (seen.has(key)) {
				// Add both the original and the current item to the duplicates set
				duplicates.add(seen.get(key));
				duplicates.add(item);
			} else {
				seen.set(key, item);
			}
		});

		const duplicatesArray = Array.from(duplicates);
		setDuplicateResults(duplicatesArray);
		return duplicatesArray;
	};
	/**
	 * search API for job categories
	 */
	const search = async () => {
		setIsSearching(true);
		const results = await searchJobCategoryMappings(
			sDepartmentSearchValue,
			sJobTitleSearchValue
		);
		findDuplicateObjects(results);
		setResults(results.length > 0 ? results : [{ ...DEFAULT_LIST_ITEM }]);
		setIsResultsExpanded(true);
		setIsSearching(false);
	};

	/**
	 * handle user interaction with clicking on search results
	 * @param {number} id primary key for job category mapping
	 */
	const onClick = async id => {
		setIsResultsExpanded(false);

		// Check if the id exists in the duplicateResults array
		const duplicate = duplicateResults
			.filter(item => item.id === id)
			.map(item => item);

		// If idsArray is not empty, set it as the selection ID
		if (duplicate.length > 0) {
			const allIds = duplicateResults
				.filter(
					result =>
						result.department === duplicate[0].department &&
						result.jobTitle === duplicate[0].jobTitle
				)
				.map(item => item.id);
			setSelectionId(allIds);
		} else {
			setSelectionId([id]);
		}
	};

	/**
	 * Job categories grouped together by department and job title.
	 * If there are multiples, they get grouped together into 1 for displaying
	 */
	const groupedResults = Array.from(
		new Map(
			sResults.map(jcm => [`${jcm.department}-${jcm.jobTitle}`, jcm])
		).values()
	);

	return (
		<PageWithBreadcrumbs currentPageLabel='Job Category Search' isSearch>
			<Fragment key={props.key}>
				<Typography
					variant='h6'
					color='primary'
					align='center'
					gutterBottom
				>
					Epic Job Category Mapping Search
				</Typography>
				<Typography variant='body1' align='center' gutterBottom>
					To index Epic job category mappings and their respective
					attributes, search below by name.
				</Typography>
				<Box
					sx={{
						display: 'flex',
						maxWidth: {
							sm: '100%',
							md: '100%',
						},
						justifyContent: 'flex-start',
						flexDirection: {
							sm: 'column',
							md: 'row',
						},
					}}
				>
					<AsyncAutocomplete
						id={`${BEM_NAME_ELEMENTS.VIEW_SEARCH}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--department-search`}
						type={AUTOCOMPLETE_SEARCH_TYPE.MAPPING_DEPARTMENT}
						label='HR Department'
						placeholder='Search for HR Departments'
						handleChange={value => {
							setDepartmentSearchValue(
								value?.label ?? value ?? ''
							);
							setResults([]);
						}}
						handleKeystroke={value => {
							setDepartmentSearchValue(value);
						}}
						value={sDepartmentSearchValue}
						sx={{ flexGrow: 1 }}
						freeSolo
					/>
					<AsyncAutocomplete
						id={`${BEM_NAME_ELEMENTS.VIEW_SEARCH}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--job-title-search`}
						type={AUTOCOMPLETE_SEARCH_TYPE.MAPPING_JOB_TITLE}
						label='Job Title'
						placeholder='Search for Job Titles'
						handleChange={value => {
							setJobTitleSearchValue(value?.label ?? value ?? '');
							setResults([]);
						}}
						handleKeystroke={value => {
							setJobTitleSearchValue(value);
						}}
						value={sJobTitleSearchValue}
						search={value =>
							searchJobCategoryMappingJobTitles(value, true)
						}
						sx={{ flexGrow: 1 }}
						freeSolo
					/>
					<Button
						variant={'contained'}
						disabled={
							!(sDepartmentSearchValue || sJobTitleSearchValue)
						}
						onClick={search}
						id={`${BEM_NAME_ELEMENTS.VIEW_SEARCH}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--job-category-mapping-search`}
					>
						Search
					</Button>
				</Box>

				{sIsSearching ? (
					<Stack direction='column' alignItems='center' gap={1}>
						<CircularProgressWithLabel label='Searching...' />
					</Stack>
				) : (
					sResults.length > 0 && (
						<Accordion
							sx={{
								borderRadius: '8px',
								marginTop: '32px',
								'&:before': {
									backgroundColor: 'unset',
								},
							}}
							disableGutters
							elevation={2}
							expanded={sIsResultsExpanded}
							onChange={() =>
								setIsResultsExpanded(state => !state)
							}
							square={false}
						>
							<AccordionSummary
								sx={{
									fontFamily: 'default',
									fontWeight: 'bold',
								}}
								expandIcon={<ExpandMoreIcon />}
							>
								Search Results
							</AccordionSummary>

							<AccordionDetails
								sx={{
									padding: '0px 8px 8px 8px',
								}}
							>
								<List
									sx={{
										maxHeight: '200px',
										overflowY: 'auto',
										paddingTop: '0px',
										paddingBottom: '0px',
									}}
									dense
								>
									{groupedResults.map((jcm, index) => (
										<ListItemButton
											id={`${BEM_NAME_ELEMENTS.VIEW_SEARCH}__${BEM_NAME_ELEMENTS.FIELD_LIST}--${jcm.id}`}
											divider={
												index !== sResults.length - 1
											}
											key={jcm.id}
											disabled={
												jcm.id === DEFAULT_LIST_ITEM.id
											}
											onClick={() => onClick(jcm.id)}
											selected={
												sSelectionId &&
												sSelectionId.includes(jcm.id)
											}
										>
											<ListItemText
												primary={
													jcm.id ===
													DEFAULT_LIST_ITEM.id
														? jcm.label
														: `${jcm.department} > ${jcm.jobTitle}`
												}
											/>
										</ListItemButton>
									))}
								</List>
							</AccordionDetails>
						</Accordion>
					)
				)}

				{sSelectionId && (
					<Box sx={{ pb: 12, mt: 2 }}>
						<Divider />
						<Button
							variant={'contained'}
							color={'primary'}
							onClick={() =>
								history.push(
									'/EpicUtilities/JobCategoryMapping/Edit?id=' +
										sSelectionId
								)
							}
							startIcon={<EditIcon />}
							sx={{ mr: 3, mt: 2 }}
						>
							Edit
						</Button>
						{sSelectionId.map(id => (
							<JobCategoryMappingDetails
								key={id}
								id={id}
								enableLinks
							/>
						))}
					</Box>
				)}
			</Fragment>
		</PageWithBreadcrumbs>
	);
};

export default SearchView;
